import React, { useState, useEffect } from 'react';
import { publicAPI } from './api';

const CancelReservation = ({ reservationId }) => {
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');

  // Validate MongoDB ObjectId format (24 hex characters)
  const isValidObjectId = (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  };

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setLoading(true);
        console.log('Fetching reservation with ID:', reservationId);
        console.log('Reservation ID type:', typeof reservationId);
        console.log('Reservation ID length:', reservationId?.length);
        
        // Validate reservation ID format
        if (!isValidObjectId(reservationId)) {
          throw new Error(`Invalid reservation ID format: ${reservationId}`);
        }
        
        const reservationData = await publicAPI.getReservation(reservationId);
        setReservation(reservationData);
      } catch (error) {
        console.error('Error fetching reservation:', error);
        console.error('Reservation ID that failed:', reservationId);
        setError('Rezervarea nu a fost găsită sau a expirat.');
      } finally {
        setLoading(false);
      }
    };

    if (reservationId) {
      fetchReservation();
    }
  }, [reservationId]);

  const handleCancel = async () => {
    if (!window.confirm('Ești sigur că vrei să anulezi această programare? Această acțiune nu poate fi anulată.')) {
      return;
    }

    try {
      setCancelling(true);
      setError('');
      
      // Validate reservation ID format
      if (!isValidObjectId(reservationId)) {
        throw new Error(`Invalid reservation ID format: ${reservationId}`);
      }
      
      const result = await publicAPI.cancelReservation(reservationId);
      
      if (result.cancelled) {
        setCancelled(true);
        // Store email status for display
        if (result.email_sent) {
          console.log('Cancellation confirmation email sent');
        } else {
          console.log('No email sent (no email address or email failed)');
        }
      } else {
        setError('Eroare la anularea programării. Vă rugăm să încercați din nou.');
      }
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      setError('Eroare la anularea programării. Vă rugăm să încercați din nou.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă detaliile programării...</p>
        </div>
      </div>
    );
  }

  if (error && !reservation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Eroare</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a 
            href="/" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Înapoi la pagina principală
          </a>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Programare Anulată</h1>
          <p className="text-gray-600 mb-4">
            Programarea ta a fost anulată cu succes. Locul a fost eliberat pentru alte persoane.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            📧 Un email de confirmare a anulării a fost trimis la adresa ta de email.
          </p>
          <a 
            href="/" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Înapoi la pagina principală
          </a>
        </div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Rezervare Nu Găsită</h1>
          <p className="text-gray-600 mb-6">Rezervarea nu a fost găsită sau a expirat.</p>
          <a 
            href="/" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Înapoi la pagina principală
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <div className="text-yellow-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Anulare Programare</h1>
          <p className="text-gray-600">Ești sigur că vrei să anulezi această programare?</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Detaliile programării:</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Nume:</span>
              <span className="font-medium">
                {reservation.person_data?.first_name} {reservation.person_data?.last_name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Apartament:</span>
              <span className="font-medium">{reservation.person_data?.apartment_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Telefon:</span>
              <span className="font-medium">{reservation.person_data?.phone}</span>
            </div>
            {reservation.person_data?.email && (
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{reservation.person_data.email}</span>
              </div>
            )}
            {reservation.selected_month && (
              <div className="flex justify-between">
                <span className="text-gray-600">Luna:</span>
                <span className="font-medium">{reservation.selected_month}</span>
              </div>
            )}
            {reservation.selected_day && (
              <div className="flex justify-between">
                <span className="text-gray-600">Ziua:</span>
                <span className="font-medium">{reservation.selected_day}</span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cancelling ? 'Se anulează...' : 'Anulează Programarea'}
          </button>
          
          <a 
            href="/" 
            className="block w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors text-center"
          >
            Nu, păstrează programarea
          </a>
        </div>

        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-xs">
            <strong>⚠️ Atenție:</strong> Anularea va elibera locul pentru alte persoane. 
            Această acțiune nu poate fi anulată.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CancelReservation;
